import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import type { SubscriptionOut } from "@/api/types";

/**
 * Попап після активації/продовження підписки. Стиль — як у решти попапів
 * (AppAlert/Support): центрований, rounded-3xl, анімований емодзі зверху +
 * заголовок + короткий опис + стандартні кнопки. Юзер 2026-06-15: «прибери
 * список переваг, зроби в одному стилі з іншими попапами».
 */
export function SubscriptionActivatedModal({
  sub,
  onClose,
}: {
  sub: SubscriptionOut;
  onClose: () => void;
}) {
  const planLabel = sub.plan === "test" ? "Тест-підписка" : "PRO";
  // Під заморозкою «сирий» ends_at не той: справжній кінець — після
  // розморозки (owner 2026-09-26: оплата під час заморозки). Безстрокова
  // адмінська пауза — дати кінця немає, лише кількість днів.
  const isFrozen = sub.status === "frozen";
  const endsIso = isFrozen ? (sub.ends_at_after_unfreeze ?? null) : sub.ends_at;
  const endsAt = endsIso ? new Date(endsIso) : null;
  const endsDate = endsAt
    ? endsAt.toLocaleDateString("uk-UA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  const daysLeft = Math.max(
    1,
    isFrozen || !endsAt
      ? Math.round(sub.days_left ?? 1)
      : Math.ceil((endsAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
  // 1 день, 2–4 дні, 5–20 днів, 21 день, 22 дні… (було «162 днів»).
  const n10 = daysLeft % 10;
  const n100 = daysLeft % 100;
  const dayWord =
    n10 === 1 && n100 !== 11
      ? "день"
      : n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)
        ? "дні"
        : "днів";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />
        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🎉" size={48} />
          </div>
          <div className="font-display text-[1.125rem] font-bold text-ink leading-tight mb-1.5">
            Підписку активовано!
          </div>
          <div className="text-[0.8125rem] text-ink-dim leading-relaxed">
            {planLabel} · {daysLeft} {dayWord}
            {endsDate ? ` — до ${endsDate}` : isFrozen ? " після розморозки" : ""}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
